'use client';
import { clearPendingLoginAttempt } from '#pplx/frontend/apps/ask/auth/loginAttemptCorrelation.js';
/// <reference types="google.accounts" />
import { signIn } from '#pplx/frontend/apps/ask/auth/signIn.js';
import { isCometBrowser } from '#pplx/frontend/comet/client/utils.js';
import { loadGoogleIdentityServicesSdk } from '#pplx/frontend/components/loadGoogleSdk.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { LOGIN_SOURCE_PARAM } from '#pplx/frontend/components/utils/consts.js';
import { getDevice } from '#pplx/frontend/platform-components/device.js';
/**
 *
 * @returns true if one tap should be cancelled, false otherwise
 */
function cancelOneTap() {
    const path = window.location.pathname;
    const conditions = [
        { match: '/search/new' },
        { match: '/join/', startsWith: true },
        { match: '/onboarding', startsWith: true },
        { match: '/auth/windows-app', startsWith: true },
        { match: '/auth/sso', startsWith: true },
        { match: '/auth/error', startsWith: true },
    ];
    return conditions.some(({ match, startsWith }) => startsWith ? path.startsWith(match) : path === match);
}
/**
 * @returns the login source for the current page
 */
function getLoginSource() {
    if (!window) {
        return ModalOrigin.GOOGLE_ONETAP_UNKNOWN;
    }
    const pathname = window.location.pathname;
    if (!pathname) {
        return ModalOrigin.GOOGLE_ONETAP_UNKNOWN;
    }
    if (pathname === '/') {
        return ModalOrigin.GOOGLE_ONETAP_HOME;
    }
    if (pathname.startsWith('/search/')) {
        return ModalOrigin.GOOGLE_ONETAP_THREAD;
    }
    if (pathname.startsWith('/page/')) {
        return ModalOrigin.GOOGLE_ONETAP_PAGE;
    }
    if (pathname.startsWith('/discover')) {
        return ModalOrigin.GOOGLE_ONETAP_DISCOVER;
    }
    return ModalOrigin.GOOGLE_ONETAP_UNKNOWN;
}
/**
 * Handles the credential response from the Google Identity Services SDK
 * @param response - The credential response from the Google Identity Services SDK
 */
async function handleCredentialResponse(response) {
    if (!response) {
        return;
    }
    const loginSource = getLoginSource();
    const callbackUrl = typeof window !== 'undefined'
        ? new URL(window.location.href)
        : new URL('/', 'https://www.perplexity.ai');
    const redirectUrl = callbackUrl.searchParams.get('callbackUrl');
    const callbackUrlParams = new URLSearchParams();
    if (redirectUrl) {
        callbackUrlParams.set('callbackUrl', redirectUrl);
    }
    callbackUrlParams.set(LOGIN_SOURCE_PARAM, loginSource);
    callbackUrl.search = callbackUrlParams.toString();
    const options = {
        credential: response.credential,
        callbackUrl: callbackUrl.toString(),
    };
    // One Tap bypasses LoginModal, so it must not consume an abandoned prompt's
    // correlation marker if the user later authenticates through this flow.
    clearPendingLoginAttempt();
    await signIn('googleonetap', options);
}
/**
 * Initializes the Google One Tap authentication flow
 */
export async function initializeGoogleOneTap() {
    const userAgent = navigator.userAgent;
    const { isMobile, isMobileSafari } = getDevice(userAgent);
    if (isMobile ||
        isMobileSafari ||
        !import.meta.env.VITE_GOOGLE_CLIENT_ID ||
        cancelOneTap() ||
        // See https://linear.app/perplexity/issue/BRO-2913/browserweb-one-tap-signin-stole-focus-from-ntp for more details
        isCometBrowser()) {
        return;
    }
    try {
        await loadGoogleIdentityServicesSdk();
    }
    catch {
        // One Tap is optional when the third-party SDK cannot be loaded.
        return;
    }
    const googleIdentity = window.google?.accounts?.id;
    if (typeof googleIdentity?.initialize !== 'function' ||
        typeof googleIdentity?.prompt !== 'function') {
        return;
    }
    const loginHint = new URLSearchParams(window.location.search).get('email') ?? undefined;
    // Keep the prompt theme independent of the iframe's CSS color-scheme reset.
    // The SDK supports color_scheme, but @types/google.accounts does not include it yet.
    const configuration = {
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        color_scheme: document.documentElement.dataset.colorScheme === 'dark' ? 'dark' : 'light',
        callback: handleCredentialResponse,
        auto_select: true,
        login_hint: loginHint,
        use_fedcm_for_prompt: true,
    };
    googleIdentity.initialize(configuration);
    googleIdentity.prompt();
}
//# sourceMappingURL=initializeGoogleOneTap.js.map